import * as turf from '@turf/turf'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import type GeoJSON from 'geojson'
import type { FeatureCollection } from 'geojson'
import type { Coordinate } from 'ol/coordinate'
import { fromLonLat } from 'ol/proj'

import { partition } from '../array'
import { sumBy } from '../number'
import { isNil, isNonNil } from '../object'
import { Polygon } from './feature-operations'
import type {
  FeatureLikeFeature,
  LineLikeFeature,
  LineLikeGeometry,
  PolyLikeFeature,
  PolyLikeGeometry
} from './types'

type Add = {
  type: 'add'
  features: FeatureLikeFeature[]
}

type Remove = {
  type: 'remove'
  features: (PolyLikeFeature | LineLikeFeature)[]
}

export type ModifyFeature = {
  id: string | number
  from: PolyLikeFeature
  to: PolyLikeFeature
}

export type ModifyLineFeature = {
  id: string | number
  from: LineLikeFeature
  to: LineLikeFeature
}

type Modify = {
  type: 'modify'
  features: (ModifyFeature | ModifyLineFeature)[]
}

export type Edit = Add | Remove | Modify

export const applyEditsToPolygons = (features: PolyLikeFeature[], edits: Edit[]) => {
  const next = edits.reduce<PolyLikeFeature[]>((current, edit) => {
    switch (edit.type) {
      case 'add':
        return [...current, ...(edit.features as PolyLikeFeature[])]
      case 'remove':
        return current.filter(
          (feature) => !edit.features.some((removed) => removed.id === feature.id)
        )
      case 'modify':
        return current.map(
          (feature) =>
            (edit.features as { id: string | number; to: PolyLikeFeature }[]).find(
              (modified) => modified.id === feature.id
            )?.to ?? feature
        )
    }
  }, features)
  return flattenFeatures(next)
}

type MergeResult =
  | { isChanged: false }
  | {
      isChanged: true
      removed: PolyLikeFeature[]
      result: PolyLikeFeature
    }

export type InsertOptions = {
  shouldUnion: (existing: PolyLikeFeature, incoming: PolyLikeFeature) => boolean
}

export type ReassignOptions = {
  shouldUnion: (existing: PolyLikeFeature, reassigned: PolyLikeFeature) => boolean
  updateProperties: (properties: PolyLikeFeature['properties']) => PolyLikeFeature['properties']
}

export const always: InsertOptions['shouldUnion'] = () => true

const mergeNoop = {
  isChanged: false
} as const

/**
 * This algorithm assumes that the collection is known to be disjoint,
 * so we only need to test for intersections against the new feature,
 * not within the collection itself.
 *
 * The result is a new feature that can replace the original feature,
 * and a list of features that were removed from the collection (i.e.,
 * subsumed by the new feature).
 */
const unionIntersectingFeature = (
  feat: PolyLikeFeature,
  collection: GeoJSON.FeatureCollection<PolyLikeFeature['geometry']>
): MergeResult => {
  if (feat.id === undefined) {
    throw new Error('Feature must have an id to be merged')
  }
  if (collection.features.length === 0) {
    return mergeNoop
  }

  // Track original intersecting features separately from the flattened parts used for union
  // computation. A feature stored in the source as a MultiPolygon (e.g. after Feature.normalize
  // repairs a self-intersecting polygon) must be removed by its original ID, not by the
  // split IDs that Polygon.flatten assigns to its parts.
  const intersecting = collection.features.filter(
    (polygon) => turf.booleanIntersects(feat, polygon) && feat.id !== polygon.id
  )

  if (intersecting.length === 0) {
    return mergeNoop
  }

  const toMerge = intersecting.flatMap((polygon) =>
    polygon.geometry.type === 'Polygon' ? polygon : Polygon.flatten(polygon)
  ) as GeoJSON.Feature<GeoJSON.Polygon>[]

  const featPolygons =
    feat.geometry.type === 'Polygon'
      ? [feat as GeoJSON.Feature<GeoJSON.Polygon>]
      : Polygon.flatten(feat)
  let processedOthers = toMerge
  const processedFeatPolygons: GeoJSON.Feature<GeoJSON.Polygon>[] = []
  for (const poly of featPolygons) {
    const [preprocessed, updatedOthers] = Polygon.ensureSharedVertices(poly, processedOthers)
    processedFeatPolygons.push(preprocessed)
    processedOthers = updatedOthers
  }
  const mergedTurfPolygon = turf.union(
    turf.featureCollection([...processedFeatPolygons, ...processedOthers])
  )

  if (isNil(mergedTurfPolygon)) {
    console.error('Failed to merge polygons, union returned null')
    return mergeNoop
  }

  mergedTurfPolygon.id = feat.id // ensure the merged polygon has the same id as the original feature

  return {
    isChanged: true,
    removed: intersecting,
    result: mergedTurfPolygon
  }
}

/**
 * Checks if a polygon or multipolygon feature is self-intersecting, i.e., if one or more regions of the polygon
 * (or its holes) overlap with themselves.
 *
 * If it is, it returns a union of the feature with itself, which can be used to visualize the self-intersection.
 * If not, it returns false.
 */
export const normalizeFeature = (feature: PolyLikeFeature) => {
  const union = turf.union(turf.featureCollection([feature, feature]))

  if (isNil(union)) {
    return feature
  }

  try {
    if (turf.booleanEqual(feature, union)) return feature
  } catch (e) {
    console.log(feature, e)
    return feature
  }

  union.id = feature.id // ensure the union has the same id as the original feature
  union.properties = feature.properties

  return union
}

export const flattenFeatures = (features: PolyLikeFeature[]) => {
  return features.flatMap((feature) => {
    if (feature.geometry.type === 'Polygon') {
      const feat = normalizeFeature(feature)
      feat.id = feature.id ?? crypto.randomUUID()
      feat.properties = feature.properties
      return feat ?? []
    }
    if (feature.geometry.type === 'MultiPolygon') {
      return turf.flatten(feature).features.map((poly) => {
        poly.id = crypto.randomUUID()
        poly.properties = feature.properties
        return normalizeFeature(poly) ?? []
      })
    }
    return []
  })
}

const toPolygonFeatures = (
  feature: PolyLikeFeature,
  options?: { preserveIdForSingleResult?: boolean }
): GeoJSON.Feature<GeoJSON.Polygon>[] => {
  const normalized = normalizeFeature(feature)

  if (normalized.geometry.type === 'Polygon') {
    normalized.id =
      options?.preserveIdForSingleResult && feature.id !== undefined
        ? feature.id
        : (normalized.id ?? crypto.randomUUID())
    normalized.properties = feature.properties
    return [normalized as GeoJSON.Feature<GeoJSON.Polygon>]
  }

  const flattened = turf.flatten(normalized).features.map((poly) => {
    poly.id = crypto.randomUUID()
    poly.properties = feature.properties
    return normalizeFeature(poly) as GeoJSON.Feature<GeoJSON.Polygon>
  })

  if (flattened.length === 1 && options?.preserveIdForSingleResult && feature.id !== undefined) {
    flattened[0].id = feature.id
  }

  return flattened
}

type Modification = {
  from: PolyLikeFeature
  to: PolyLikeFeature
  id: string | number
}

export enum SplitFailureReason {
  NotEnoughParts = 'not-enough-parts',
  Overlap = 'overlap',
  CoverageMismatch = 'coverage-mismatch',
  Exception = 'exception'
}

export type SplitAttempt =
  | {
      isSplittable: false
      parts: PolyLikeFeature[]
      reason: SplitFailureReason
      error?: string
      originalAreaSqMeters?: number
      overlapAreaSqMeters?: number
      overlapCentroid?: GeoJSON.Position
      overlapBbox?: [number, number, number, number]
      missingAreaSqMeters?: number
      extraAreaSqMeters?: number
    }
  | {
      isSplittable: true
      parts: PolyLikeFeature[]
    }

const splitHelper: (
  feature: PolyLikeFeature,
  splitterLine: GeoJSON.Feature<GeoJSON.LineString>
) => PolyLikeFeature[] = (feature, splitterLine) => {
  if (feature.geometry.type === 'MultiPolygon') {
    return turf
      .flatten(feature)
      .features.flatMap((feat) => (turf.area(feat) > 0.1 ? splitHelper(feat, splitterLine) : []))
  }

  return Polygon.split(feature as GeoJSON.Feature<GeoJSON.Polygon>, splitterLine) ?? [feature]
}

const hasMeaningfulArea = (
  feature: GeoJSON.Feature | null | undefined,
  originalAreaSqMeters: number
): feature is GeoJSON.Feature => {
  if (isNil(feature)) {
    return false
  }
  return turf.area(feature) > Math.max(1, originalAreaSqMeters * 10 ** -7)
}

export const attemptSplitFeature = (
  feature: PolyLikeFeature,
  splitterLine: GeoJSON.Feature<GeoJSON.LineString>
): SplitAttempt => {
  try {
    if (feature.geometry.type === 'MultiPolygon') {
      const attempts = turf.flatten(feature).features.map((part) =>
        attemptSplitFeature(
          {
            ...part,
            properties: feature.properties
          },
          splitterLine
        )
      )

      const changedAttempts = attempts.filter((attempt) => attempt.isSplittable)
      if (changedAttempts.length === 0) {
        return { isSplittable: false, parts: [feature], reason: SplitFailureReason.NotEnoughParts }
      }

      const unchangedParts = turf
        .flatten(feature)
        .features.filter(
          (_, index) => !attempts[index]?.isSplittable
        ) as GeoJSON.Feature<GeoJSON.Polygon>[]
      const combinedParts = [
        ...unchangedParts.map((part) => ({
          ...part,
          properties: feature.properties
        })),
        ...changedAttempts.flatMap((attempt) => attempt.parts)
      ]
      return validateSplitAttempt(feature, combinedParts)
    }

    const exterior = turf.lineString(feature.geometry.coordinates[0])
    const exteriorCrossings = turf.lineIntersect(exterior, splitterLine).features.length
    if (exteriorCrossings === 0 && !turf.booleanWithin(splitterLine, feature)) {
      return { isSplittable: false, parts: [feature], reason: SplitFailureReason.NotEnoughParts }
    }

    const parts = splitHelper(feature, splitterLine).flatMap((part) =>
      toPolygonFeatures(
        {
          ...part,
          properties: feature.properties
        },
        { preserveIdForSingleResult: true }
      )
    )

    return validateSplitAttempt(feature, parts)
  } catch (error) {
    return {
      isSplittable: false,
      parts: [feature],
      reason: SplitFailureReason.Exception,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

const validateSplitAttempt = (feature: PolyLikeFeature, parts: PolyLikeFeature[]): SplitAttempt => {
  if (parts.length <= 1) {
    return { isSplittable: false, parts, reason: SplitFailureReason.NotEnoughParts }
  }

  const original = normalizeFeature(feature)
  const originalArea = turf.area(original)

  for (let i = 0; i < parts.length; i++) {
    for (let j = i + 1; j < parts.length; j++) {
      const overlap = turf.intersect(turf.featureCollection([parts[i], parts[j]]))
      const overlapAreaSqMeters = isNil(overlap) ? 0 : turf.area(overlap)
      if (hasMeaningfulArea(overlap, originalArea)) {
        const overlapCentroid = turf.centroid(overlap).geometry.coordinates
        const overlapBbox = turf.bbox(overlap) as [number, number, number, number]
        return {
          isSplittable: false,
          parts,
          reason: SplitFailureReason.Overlap,
          originalAreaSqMeters: originalArea,
          overlapAreaSqMeters,
          overlapCentroid,
          overlapBbox
        }
      }
    }
  }

  const reconstructed = normalizeCollection(turf.featureCollection(parts))

  if (isNil(reconstructed)) {
    return { isSplittable: false, parts, reason: SplitFailureReason.CoverageMismatch }
  }

  const missing = turf.difference(turf.featureCollection([original, reconstructed]))
  const extra = turf.difference(turf.featureCollection([reconstructed, original]))
  const missingAreaSqMeters = isNil(missing) ? 0 : turf.area(missing)
  const extraAreaSqMeters = isNil(extra) ? 0 : turf.area(extra)

  if (hasMeaningfulArea(missing, originalArea) || hasMeaningfulArea(extra, originalArea)) {
    return {
      isSplittable: false,
      parts,
      reason: SplitFailureReason.CoverageMismatch,
      originalAreaSqMeters: originalArea,
      missingAreaSqMeters,
      extraAreaSqMeters
    }
  }

  return { isSplittable: true, parts }
}

export const GuidanceCollection = {}

// Draw sources can hold guidance-line features (LineStrings, and Points for
// decorated AHeading anchors) alongside the zone polygons — a zone owns its
// guidance line, so the two coexist in one collection during zone editing.
// The polygon set operations below must never see them: a LineString reaching
// turf.union/difference throws (or nulls out), and the whole edit silently
// no-ops.
const polygonFeatures = (
  collection: GeoJSON.FeatureCollection<PolyLikeGeometry>
): GeoJSON.FeatureCollection<PolyLikeGeometry> => ({
  type: 'FeatureCollection',
  features: collection.features.filter(
    (f) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
  )
})

const overlapsInZone = (a: PolyLikeFeature, b: PolyLikeFeature) =>
  a.properties?.zone === b.properties?.zone &&
  turf.booleanIntersects(a, b) &&
  isNonNil(turf.intersect(turf.featureCollection([a, b])))

const mergeOverlapsInZone = (features: PolyLikeFeature[]) =>
  features.reduce<PolyLikeFeature[]>((disjointSoFar, feature) => {
    const [overlapping, unaffected] = partition(disjointSoFar, (existing) =>
      overlapsInZone(existing, feature)
    )
    const [first] = overlapping
    if (first === undefined) return [...disjointSoFar, feature]

    const merged = normalizeCollection(turf.featureCollection([...overlapping, feature]))
    if (isNil(merged)) return [...disjointSoFar, feature]

    merged.id = first.id
    merged.properties = first.properties
    return [...unaffected, merged]
  }, [])

const withoutInnerRings = (feature: PolyLikeFeature): PolyLikeFeature => {
  if (feature.geometry.type === 'Polygon') {
    if (feature.geometry.coordinates.length <= 1) return feature
    return {
      ...feature,
      geometry: { ...feature.geometry, coordinates: [feature.geometry.coordinates[0]] }
    }
  }
  if (!feature.geometry.coordinates.some((poly) => poly.length > 1)) return feature
  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: feature.geometry.coordinates.map((poly) => [poly[0]])
    }
  }
}

const modifyFeature = (
  { from, to }: { from: PolyLikeFeature; to: PolyLikeFeature },
  collection: FeatureCollection<PolyLikeGeometry>
): Edit[] => {
  if (to.id === undefined) {
    throw new Error('Feature must have an id to be modified')
  }

  const normalizedTargets = toPolygonFeatures(to, { preserveIdForSingleResult: true })
  const normalization: Edit[] =
    normalizedTargets.length === 1
      ? [
          {
            type: 'modify',
            features: [
              {
                id: to.id,
                from,
                to: normalizedTargets[0]
              }
            ]
          }
        ]
      : [
          { type: 'remove', features: [from] },
          { type: 'add', features: normalizedTargets }
        ]

  const mergeResult = unionIntersectingFeature(to, polygonFeatures(collection))

  if (!mergeResult.isChanged) {
    // no further changes needed
    return normalization
  }

  const mergedTargets = toPolygonFeatures(mergeResult.result, { preserveIdForSingleResult: true })

  if (mergedTargets.length === 1) {
    return [
      ...normalization,
      {
        type: 'modify',
        features: [
          {
            id: to.id,
            from,
            to: mergedTargets[0]
          }
        ]
      },
      { type: 'remove', features: mergeResult.removed }
    ]
  }

  return [
    { type: 'remove', features: [from, ...mergeResult.removed] },
    {
      type: 'add',
      features: mergedTargets
    }
  ]
}

export const DisjointCollection = {
  /**
   * Initializes a collection with a set of features, merging features in the same zone whose
   * areas overlap, such as the coverage of two passes, so that the collection starts disjoint.
   * Features that overlap nothing keep their ids and properties. Features in different zones are
   * assumed not to overlap. Also normalizes the features to ensure they are valid and
   * non-self-intersecting, flattening any multipolygons into polygons.
   */
  initialize: (collection: FeatureCollection<PolyLikeGeometry>): Edit[] => {
    return mergeOverlapsInZone(collection.features).map((feature) => ({
      type: 'add',
      features: toPolygonFeatures(feature, { preserveIdForSingleResult: true })
    }))
  },

  /**
   * Clips a new feature to fit within the area of a collection of features.
   * return undefined if the feature is out of bounds
   */
  clip: (
    feature: PolyLikeFeature,
    collection: GeoJSON.FeatureCollection<PolyLikeGeometry>
  ): PolyLikeFeature | undefined => {
    if (feature.id === undefined) {
      throw new Error('Feature must have an id to be clipped')
    }
    const polygons = polygonFeatures(collection)
    if (polygons.features.length === 0) return undefined
    const boundary =
      polygons.features.length === 1 ? normalizeFeature(polygons.features[0]) : turf.union(polygons)
    if (isNil(boundary)) return undefined

    const intersection = turf.intersect(turf.featureCollection([feature, boundary]))

    if (isNil(intersection)) return undefined

    intersection.id = feature.id
    return intersection
  },
  /**
   * Adds a new feature to a collection of non-overlapping features.
   * If the feature intersects with any existing features, it merges them into a single feature.
   * If the feature does not intersect with any existing features, it is added as a new feature.
   * If the feature is already present in the collection, it is not added again.
   */
  insert: (
    feature: PolyLikeFeature,
    collection: GeoJSON.FeatureCollection<PolyLikeGeometry>,
    { shouldUnion }: InsertOptions
  ): Edit[] => {
    if (feature.id === undefined) {
      throw new Error('Feature must have an id to be inserted')
    }

    feature = normalizeFeature(feature)

    if (collection.features.some((f) => f.id === feature.id)) {
      // If the feature is already in the collection, we do not add it again
      console.error(`Feature with id ${feature.id} already exists in the collection`)
      return []
    }

    const polygons = polygonFeatures(collection)

    if (polygons.features.length === 0) {
      return [
        {
          type: 'add',
          features: toPolygonFeatures(feature, { preserveIdForSingleResult: true })
        }
      ]
    }

    const intersecting = polygons.features.filter(
      (polygon) => turf.booleanIntersects(feature, polygon) && feature.id !== polygon.id
    )

    if (intersecting.length === 0) {
      return [
        {
          type: 'add',
          features: toPolygonFeatures(feature, { preserveIdForSingleResult: true })
        }
      ]
    }

    const featuresToUnion = intersecting.filter((existing) => shouldUnion(existing, feature))
    const featuresToCarve = intersecting.filter((existing) => !shouldUnion(existing, feature))

    const mergeResult =
      featuresToUnion.length > 0
        ? unionIntersectingFeature(feature, turf.featureCollection(featuresToUnion))
        : mergeNoop

    const mergedFeature = mergeResult.isChanged ? mergeResult.result : feature

    if (isNil(mergedFeature)) {
      console.error('Failed to merge inserted features')
      return []
    }

    mergedFeature.properties = feature.properties
    mergedFeature.id = feature.id

    const modifiedFeatures: ModifyFeature[] = []
    const removedFeatures = [...featuresToUnion]
    const addedFeatures = toPolygonFeatures(mergedFeature, { preserveIdForSingleResult: true })

    featuresToCarve.forEach((existing) => {
      if (isNil(existing.id)) return

      const difference = turf.difference(turf.featureCollection([existing, mergedFeature]))

      if (isNil(difference)) {
        removedFeatures.push(existing)
        return
      }

      difference.id = existing.id
      difference.properties = existing.properties

      if (difference.geometry.type === 'Polygon') {
        modifiedFeatures.push({
          id: existing.id,
          from: existing,
          to: difference
        })
        return
      }

      addedFeatures.push(...toPolygonFeatures(difference))
      removedFeatures.push(existing)
    })

    const edits: Edit[] = [
      {
        type: 'add',
        features: addedFeatures
      }
    ]

    if (removedFeatures.length > 0) {
      edits.push({ type: 'remove', features: removedFeatures })
    }

    if (modifiedFeatures.length > 0) {
      edits.push({ type: 'modify', features: modifiedFeatures })
    }

    return edits
  },

  /**
   * Builds the edits that commit features the user reshaped in one gesture, such as polygons
   * sharing a dragged vertex, merging each with whatever it now overlaps.
   */
  modify: ({
    modifications,
    collection
  }: {
    modifications: { from: PolyLikeFeature; to: PolyLikeFeature }[]
    collection: FeatureCollection<PolyLikeGeometry>
  }): Edit[] => {
    // Undo restores each dragged feature from this edit: undoing a removal puts back the feature
    // as it was when the command was built, which already holds the dragged geometry.
    const recorded: Edit = {
      type: 'modify',
      features: modifications.map(({ from, to }) => {
        if (to.id === undefined) throw new Error('Feature must have an id to be modified')
        return { id: to.id, from, to }
      })
    }
    const edits: Edit[] = [recorded]
    let current = collection

    for (const modification of modifications) {
      if (!current.features.some((feature) => feature.id === modification.to.id)) continue

      const next = modifyFeature(modification, current)
      edits.push(...next)
      current = { ...current, features: applyEditsToPolygons(current.features, next) }
    }

    return edits
  },

  erase: (
    featureToRemove: PolyLikeFeature,
    collection: FeatureCollection<PolyLikeGeometry>
  ): Edit[] => {
    if (featureToRemove.id === undefined) {
      throw new Error('Feature must have an id to be erased')
    }

    if (collection.features.length === 0) {
      return []
    }
    const removed: PolyLikeFeature[] = []
    const modified: Modification[] = []
    const added: PolyLikeFeature[] = []

    polygonFeatures(collection).features.forEach((feature) => {
      const isIntersecting = turf.booleanIntersects(featureToRemove, feature)
      if (!isIntersecting || featureToRemove.id === feature.id || isNil(feature.id)) {
        // if the feature does not intersect with the polygon or is the same feature, we skip it
        return
      }

      const difference = turf.difference(turf.featureCollection([feature, featureToRemove]))

      if (isNil(difference)) {
        // if the difference is null, it means the feature was completely removed
        removed.push(feature)
      } else {
        if (difference.geometry.type === 'Polygon') {
          difference.id = feature.id // ensure the modified feature has the same id as the original feature
          modified.push({
            id: feature.id,
            from: feature,
            to: difference
          })
        } else {
          removed.push(feature)
          added.push(...toPolygonFeatures(difference))
        }
      }
    })

    const edits: Edit[] = []

    if (removed.length > 0) {
      edits.push({ type: 'remove', features: removed })
    }

    if (modified.length > 0) {
      edits.push({ type: 'modify', features: modified })
    }
    if (added.length > 0) {
      edits.push({ type: 'add', features: added })
    }

    return edits
  },

  split: (
    featureToSplitBy: GeoJSON.Feature<GeoJSON.LineString>,
    collection: FeatureCollection<PolyLikeGeometry>,
    zone?: string
  ): Edit[] => {
    const add: PolyLikeFeature[] = []
    const remove: PolyLikeFeature[] = []

    polygonFeatures(collection)
      .features.filter((feat) => isNil(zone) || zone === feat.properties?.zone)
      .forEach((feature) => {
        if (isNil(feature.id) || feature.id === featureToSplitBy.id) return
        const attempt = attemptSplitFeature(feature, featureToSplitBy)
        if (attempt.isSplittable) {
          remove.push(feature)
          attempt.parts.forEach((zone) => {
            add.push(...toPolygonFeatures(zone))
          })
        }
      })

    return [
      { type: 'add', features: add },
      { type: 'remove', features: remove }
    ]
  },

  reassign: (
    feat: PolyLikeFeature,
    collection: FeatureCollection<PolyLikeGeometry>,
    { shouldUnion, updateProperties }: ReassignOptions
  ): Edit[] => {
    if (isNil(feat.id)) {
      throw new Error('Feature must have an id to be reassigned')
    }

    const reassignedProperties = updateProperties(feat.properties)

    if (JSON.stringify(reassignedProperties ?? null) === JSON.stringify(feat.properties ?? null)) {
      return []
    }

    const reassignedFeature = {
      ...feat,
      properties: reassignedProperties
    }

    const matchingFeatures = polygonFeatures(collection).features.filter(
      (existing) => existing.id !== feat.id && shouldUnion(existing, reassignedFeature)
    )

    const mergeResult = unionIntersectingFeature(feat, turf.featureCollection(matchingFeatures))

    if (!mergeResult.isChanged) {
      const reassignedParts = toPolygonFeatures(reassignedFeature, {
        preserveIdForSingleResult: true
      })
      if (reassignedParts.length === 1) {
        return [
          {
            type: 'modify',
            features: [{ id: feat.id, from: feat, to: reassignedParts[0] }]
          }
        ]
      }
      return [
        {
          type: 'remove',
          features: [feat]
        },
        {
          type: 'add',
          features: reassignedParts
        }
      ]
    }

    mergeResult.result.properties = reassignedProperties
    const reassignedParts = toPolygonFeatures(mergeResult.result, {
      preserveIdForSingleResult: true
    })

    if (reassignedParts.length === 1) {
      return [
        {
          type: 'modify',
          features: [{ id: feat.id, from: feat, to: reassignedParts[0] }]
        },
        { type: 'remove', features: mergeResult.removed }
      ]
    }

    return [
      {
        type: 'remove',
        features: [feat, ...mergeResult.removed]
      },
      {
        type: 'add',
        features: reassignedParts
      }
    ]
  },

  /**
   * Unions all polygon features that belong to the given zone into as few
   * features as possible. Polygons that share an edge collapse into one.
   * Returns an empty array when the zone already has one feature or fewer.
   */
  mergeZone: (zoneId: string, collection: FeatureCollection<PolyLikeGeometry>): Edit[] => {
    const zoneFeatures = polygonFeatures(collection).features.filter(
      (f) => f.properties?.zone === zoneId
    )
    if (zoneFeatures.length <= 1) return []

    const merged = turf.union(
      turf.featureCollection(zoneFeatures) as GeoJSON.FeatureCollection<GeoJSON.Polygon>
    )
    if (isNil(merged)) return []

    merged.properties = zoneFeatures[0].properties
    merged.id = zoneFeatures[0].id
    const mergedParts = toPolygonFeatures(merged as PolyLikeFeature, {
      preserveIdForSingleResult: true
    })

    if (mergedParts.length >= zoneFeatures.length) return []

    return [
      { type: 'remove', features: zoneFeatures },
      { type: 'add', features: mergedParts }
    ]
  },

  splitAtNecks: (minWidthM: number, collection: FeatureCollection<PolyLikeGeometry>): Edit[] => {
    const splits = polygonFeatures(collection)
      .features.filter((f): f is GeoJSON.Feature<GeoJSON.Polygon> => f.geometry.type === 'Polygon')
      .map((from) => ({ from, parts: Polygon.splitAtNecks(from, minWidthM) }))
      .filter(({ parts }) => parts.length > 1)
    if (splits.length === 0) return []

    return [
      { type: 'remove', features: splits.map(({ from }) => from) },
      { type: 'add', features: splits.flatMap(({ parts }) => parts) }
    ]
  },

  /**
   * Removes inner rings (holes) from all polygon features in the collection,
   * preserving the outer boundary of each feature.
   */
  clearInnerRings: (collection: FeatureCollection<PolyLikeGeometry>): Edit[] => {
    const polygons = polygonFeatures(collection).features.filter(
      (feature): feature is PolyLikeFeature & { id: string | number } => isNonNil(feature.id)
    )
    const filled = polygons.map(withoutInnerRings)
    const hadNoHoles = filled.every((feature, i) => feature === polygons[i])
    if (hadNoHoles) return []

    const disjoint = mergeOverlapsInZone(filled.toSorted((a, b) => turf.area(b) - turf.area(a)))
    const removed = polygons.filter(({ id }) => !disjoint.some((feature) => feature.id === id))
    const modified = disjoint.flatMap((to): ModifyFeature[] => {
      const from = polygons.find(({ id }) => id === to.id)
      if (isNil(from)) return []
      const unchanged = from === to
      return unchanged ? [] : [{ id: from.id, from, to }]
    })

    return [
      ...(removed.length > 0 ? [{ type: 'remove' as const, features: removed }] : []),
      ...(modified.length > 0 ? [{ type: 'modify' as const, features: modified }] : [])
    ]
  }
}

const getExtraProperties = (type: GuidancePrimitive, points: [Coordinate, Coordinate]) => {
  switch (type) {
    case GuidancePrimitive.ABLine:
    case GuidancePrimitive.AHeading: {
      // Web Mercator planar bearing so `heading` matches the `headingDeg`
      // written by decorateGuidanceFeature (same projection, same atan2).
      // Raw-lon/lat atan2 disagreed with the on-screen angle at any non-
      // trivial latitude.
      const [x1, y1] = fromLonLat(points[0] as [number, number])
      const [x2, y2] = fromLonLat(points[1] as [number, number])
      const dx = x2 - x1
      const dy = y2 - y1
      return {
        heading: (((Math.atan2(dx, dy) * (180 / Math.PI)) % 360) + 360) % 360,
        definedBy: 'points'
      }
    }
    case GuidancePrimitive.RadialPivot:
      return { radiusFt: Math.hypot(points[0][0] - points[1][0], points[0][1] - points[1][1]) }
    default:
      return {}
  }
}

export const LinePivotCollection = {
  add: (
    feature: GeoJSON.Feature<GeoJSON.LineString>,
    collection: GeoJSON.FeatureCollection<LineLikeGeometry | PolyLikeGeometry>,
    type: GuidancePrimitive,
    zone: string | undefined
  ): Edit[] => {
    if (feature.id === undefined) {
      throw new Error('Feature must have an id to be added')
    }

    if (collection.features.some((f) => f.id === feature.id)) {
      // If the feature is already in the collection, we do not add it again
      console.error(`Feature with id ${feature.id} already exists in the collection`)
      return []
    }

    feature.properties = {
      ...feature.properties,
      type,
      zone,
      ...getExtraProperties(type, feature.geometry.coordinates as [Coordinate, Coordinate])
    }

    // Only allow one line per zone
    const existingLinesInZone = collection.features.filter(
      (feat) =>
        (feat.geometry.type === 'LineString' || (feat.geometry.type as string) === 'Point') &&
        isNonNil(feat.id) &&
        feat.id !== feature.id &&
        feat.properties?.zone === zone
    )

    const edits: Edit[] = []
    if (existingLinesInZone.length > 0) {
      // Remove edits are keyed by feature id; Point features from AHeading
      // decoration ride along here even though they aren't LineLikeFeature.
      edits.push({ type: 'remove', features: existingLinesInZone as LineLikeFeature[] })
    }
    edits.push({ type: 'add', features: [feature] })
    return edits
  },
  /**
   * Checks if a feature modification would require changes to a disjoint collection,
   * beyond the feature's own modification.
   */
  modify: ({ from, to }: { from: LineLikeFeature; to: LineLikeFeature }): Edit[] => {
    if (to.id === undefined) {
      throw new Error('Feature must have an id to be modified')
    }

    const normalization: Edit = {
      type: 'modify',
      features: [
        {
          id: to.id,
          from: from,
          to: to
        }
      ]
    }

    return [normalization]
  },
  /**
   * Swap an existing line/pivot feature to a different primitive type. The
   * remove and add halves ride together in one Edit[] so `commandManager`
   * treats them atomically — undo restores the original type in a single
   * step. `add`'s id-collision guard would otherwise silently drop the add
   * half if the caller re-inserted under the same id.
   */
  convert: ({
    from,
    to,
    type,
    zone
  }: {
    from: LineLikeFeature
    to: GeoJSON.Feature<GeoJSON.LineString>
    type: GuidancePrimitive
    zone: string | undefined
  }): Edit[] => {
    if (to.id === undefined) {
      throw new Error('Feature must have an id to be converted')
    }
    to.properties = {
      ...to.properties,
      type,
      zone,
      ...getExtraProperties(type, to.geometry.coordinates as [Coordinate, Coordinate])
    }
    return [
      { type: 'remove', features: [from] },
      { type: 'add', features: [to] }
    ]
  }
}

/**
 * Helper function that takes a polygon's coordinates and returns
 * an array of inner rings (holes) as GeoJSON polygon features.
 *
 * This could be privately scoped and not included in the public API
 * of the Geo utility set.
 */
const innerRingsFromPolyCoords = (coords: GeoJSON.Polygon['coordinates']) => {
  return coords.slice(1).map((p) => turf.polygon([p]))
}

/**
 * Get the holes (inner rings) of a polygon or multipolygon feature, if any.
 */
const innerRings = (feat: GeoJSON.Feature<GeoJSON.Geometry>) => {
  if (feat.geometry.type === 'Polygon') {
    return innerRingsFromPolyCoords(feat.geometry.coordinates)
  }
  if (feat.geometry.type === 'MultiPolygon') {
    return feat.geometry.coordinates.flatMap(innerRingsFromPolyCoords)
  }
  return []
}

const ACRES_PER_SQ_METER = 1 / 4046.8564224

/**
 * Calculates the area of a feature collection in acres.
 * It returns an object with the total area covered and the area of holes (inner rings).
 */
export const getAreas = (featureCollection: FeatureCollection) => {
  const guidanceFeats: { id: string; type: GuidancePrimitive; zone: string }[] = []
  const zones = featureCollection.features.reduce<Record<string, number>>((acc, feature) => {
    if (isNil(feature.id)) {
      return acc
    }
    if (feature.geometry.type === 'LineString') {
      guidanceFeats.push({
        id: `${feature.id}`,
        type: feature.properties?.type as GuidancePrimitive,
        zone: feature.properties?.zone
      })
    }
    const zone = feature.properties?.zone

    if (isNil(zone)) {
      return acc
    }

    if (!acc[zone]) {
      acc[zone] = 0
    }
    acc[zone] += turf.area(feature) * ACRES_PER_SQ_METER

    return acc
  }, {})
  const areaM2 = turf.area(featureCollection)

  const holeRings = featureCollection.features.flatMap(innerRings)
  const innerAreaM2 = sumBy(holeRings, turf.area)

  return {
    holes: innerAreaM2 * ACRES_PER_SQ_METER,
    holeCount: holeRings.length,
    covered: areaM2 * ACRES_PER_SQ_METER,
    zones,
    guidanceFeats
  }
}

export type GuidanceLineFeature = {
  /** Feature UUID from the draw tool source. */
  id: string
  type: GuidancePrimitive
  zone: string | undefined
  /** Point A in [longitude, latitude] EPSG:4326 order. */
  pointA: [number, number]
  /** Point B in [longitude, latitude] EPSG:4326 order. */
  pointB: [number, number]
  /** Radius in feet — only present when type is RadialPivot. */
  radiusFt?: number
}

/**
 * Extracts guidance line coordinates from a feature collection.
 * Returns one entry per LineString feature, with both endpoints in
 * [longitude, latitude] (GeoJSON/EPSG:4326) order.
 */
export const getGuidanceLines = (featureCollection: FeatureCollection): GuidanceLineFeature[] => {
  const lines: GuidanceLineFeature[] = []
  for (const feature of featureCollection.features) {
    if (feature.geometry.type !== 'LineString' || isNil(feature.id)) continue
    const coords = feature.geometry.coordinates
    if (coords.length < 2) continue
    const type = feature.properties?.type
    const pointA = coords[0] as [number, number]
    const pointB = coords.at(-1) as [number, number]
    const radiusFt =
      type === GuidancePrimitive.RadialPivot
        ? turf.distance(turf.point(pointA), turf.point(pointB), { units: 'feet' })
        : undefined
    lines.push({
      id: `${feature.id}`,
      type,
      zone: feature.properties?.zone ?? undefined,
      pointA,
      pointB,
      radiusFt
    })
  }
  return lines
}

export const normalizeCollection = (featureCollection: FeatureCollection<PolyLikeGeometry>) =>
  featureCollection.features.length > 1
    ? turf.union(featureCollection)
    : featureCollection.features[0]

// True when `feature` shares any territory with at least one of the given
// polygons. Points are inside-or-on-boundary; lines and polygons intersect.
// The polygon list is filtered out empty first so callers can pass an
// unconditional slice without a length guard.
export const featureOverlapsAnyPolygon = (
  feature: GeoJSON.Feature,
  polygons: GeoJSON.Feature<GeoJSON.Polygon>[]
): boolean => {
  if (polygons.length === 0) return false
  return polygons.some((poly) => {
    if (feature.geometry.type === 'Point') {
      return turf.booleanPointInPolygon(feature as GeoJSON.Feature<GeoJSON.Point>, poly)
    }
    return turf.booleanIntersects(feature, poly)
  })
}
